package net.sergeych.ugdx

import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.crypto2.UniversalPublicKey
import net.sergeych.kiloparsec.command
import net.sergeych.mptools.CachedExpression
import net.sergeych.ugdx.shared.ErrorCode
import net.sergeych.ugdx.shared.api.invoke

/**
 * Party is a "public" representatino of a client that is not connected to its loginName or any searchable id.
 * It is used to disclose no more and no less information to identify participants of invoices, transfers, etc.
 * @param id partyId, some random and unique immutable string assigned bu the service.
 * @param name the most recent name of the party (varies, the client can change its name)
 * @param optPublicKey internal usage only, pleas use [publicKey] instead
 *
 */
@Serializable
data class Party(
    val id: String,
    val name: String?,
    val optPublicKey: UniversalPublicKey? = null,
) {
    @Transient
    private var _publicKey = CachedExpression(initialValue = optPublicKey)

    /**
     * Get the public key, for example, to check signature or encrypt a private message to the party. Requests and
     * caches a key if need.
     */
    suspend fun publicKey(): UniversalPublicKey =
        _publicKey.optGet {
            optPublicKey ?: cmdPartyGetKey.invoke(PartyIdArgs(id))
        }!!

    /**
     * Return most recent data about the party.
     */
    suspend fun refresh(): Party {
        return get(id, _publicKey.cachedOrNull() != null )
    }

    val verifyingKeyId by lazy { optPublicKey?.verifyingKey?.id }

    fun overridePublicKey(publicKey: UniversalPublicKey) {
        if( publicKey.verifyingKey.id != verifyingKeyId ) ErrorCode.INVALID_PARAMETER.raise("public key does not match party")
        _publicKey = CachedExpression(initialValue = publicKey)
    }

    override fun equals(other: Any?): Boolean {
        return if( other is Party)
            return other.id == id
        else false
    }

    override fun hashCode(): Int {
        return id.hashCode()
    }

    companion object {
        suspend fun get(id: String, includeKey: Boolean = false): Party = cmdPartyGet(PartyIdArgs(id, includeKey))
    }
}

@Serializable
data class PartyIdArgs(val partyId: String, val extended: Boolean = false)

@Serializable
val cmdPartyGetKey by command<PartyIdArgs, UniversalPublicKey>()

val cmdPartyGet by command<PartyIdArgs, Party>()
val cmdPartyGetByLogin by command<PartyIdArgs, Party>()

//we hold main key safely in ACO, so how?
//val cmdUpdateMainKey by command<VerifyingPublicKey, Unit>()